/**
 * Sync note metadata edits back to source FHIR resources and record AuditEvent.
 */
import type { MedplumClient } from '@medplum/core';
import type { Appointment, AuditEvent, ChargeItem, Encounter } from '@medplum/fhirtypes';
import {
  applyMetadataBillingToChargeItem,
  applyMetadataStartEndToAppointment,
  diffNoteMetadata,
} from '../noteMetadataMapping';
import type { NoteMetadataSnapshot } from '@/schemas/note-metadata.schema';
import { formatMedplumClientError } from './medplumClientErrors';

/** Drop UI-only fields (e.g. `_localHasNote`) before persisting Appointment to Medplum. */
export function stripLocalAppointmentFields(appointment: Appointment): Appointment {
  const cleaned = { ...appointment } as Record<string, unknown>;
  for (const key of Object.keys(cleaned)) {
    if (key.startsWith('_')) delete cleaned[key];
  }
  return cleaned as unknown as Appointment;
}

export interface SyncNoteMetadataInput {
  medplum: MedplumClient;
  questionnaireResponseId: string;
  before: NoteMetadataSnapshot | undefined;
  after: NoteMetadataSnapshot;
  appointment: Appointment;
  encounter?: Encounter | null;
  chargeItems?: ChargeItem[];
  agentReference?: string;
}

export interface SyncNoteMetadataResult {
  appointment?: Appointment;
  encounter?: Encounter;
  chargeItems?: ChargeItem[];
  changedFields: string[];
}

async function recordMetadataAuditEvent(
  medplum: MedplumClient,
  input: {
    questionnaireResponseId: string;
    appointmentId?: string;
    changedFields: string[];
    agentReference?: string;
  },
): Promise<void> {
  const entities: AuditEvent['entity'] = [
    {
      what: { reference: `QuestionnaireResponse/${input.questionnaireResponseId}` },
      role: { system: 'http://terminology.hl7.org/CodeSystem/object-role', code: '1', display: 'Patient Record' },
    },
  ];
  if (input.appointmentId) {
    entities.push({
      what: { reference: `Appointment/${input.appointmentId}` },
      role: { system: 'http://terminology.hl7.org/CodeSystem/object-role', code: '1', display: 'Patient Record' },
    });
  }

  await medplum.createResource<AuditEvent>({
    resourceType: 'AuditEvent',
    type: {
      system: 'http://terminology.hl7.org/CodeSystem/audit-event-type',
      code: '8',
      display: 'Security Audit',
    },
    action: 'U',
    recorded: new Date().toISOString(),
    agent: input.agentReference
      ? [{ who: { reference: input.agentReference }, requestor: true }]
      : [{ who: { display: 'medplum-ui' }, requestor: true }],
    source: { observer: { display: 'Unify Appointment Note' } },
    entity: entities,
    outcome: '0',
    outcomeDesc: `Note metadata updated: ${input.changedFields.join(', ')}`,
  });
}

export async function syncNoteMetadataEditsToSources(
  input: SyncNoteMetadataInput,
): Promise<SyncNoteMetadataResult> {
  const changedFields = diffNoteMetadata(input.before, input.after);
  if (changedFields.length === 0) {
    return { changedFields: [] };
  }

  const result: SyncNoteMetadataResult = { changedFields };
  const { medplum, appointment, encounter, chargeItems = [] } = input;
  const syncFailures: string[] = [];

  // Date/start are read-only from Appointment; duration and other fields sync on save.
  const appointmentFields = ['durationMinutes', 'attendance', 'modality', 'locations'];
  if (changedFields.some((f) => appointmentFields.includes(f)) && appointment.id) {
    try {
      const updatedAppt = applyMetadataStartEndToAppointment(appointment, input.after);
      result.appointment = await medplum.updateResource(stripLocalAppointmentFields(updatedAppt));
    } catch (err) {
      syncFailures.push(`Appointment update failed (${formatMedplumClientError(err)})`);
    }
  }

  const encounterFields = ['durationMinutes'];
  if (
    encounter?.id &&
    changedFields.some((f) => encounterFields.includes(f)) &&
    input.after.start
  ) {
    try {
      const durationMs =
        input.after.durationMinutes != null ? input.after.durationMinutes * 60_000 : undefined;
      const period = {
        ...(encounter.period ?? {}),
        start: input.after.start,
        end:
          durationMs != null && Number.isFinite(Date.parse(input.after.start))
            ? new Date(Date.parse(input.after.start) + durationMs).toISOString()
            : encounter.period?.end,
      };
      result.encounter = await medplum.updateResource({ ...encounter, period });
    } catch (err) {
      syncFailures.push(`Encounter update failed (${formatMedplumClientError(err)})`);
    }
  }

  const billingFields = ['billingCodes', 'units'];
  if (changedFields.some((f) => billingFields.includes(f)) && chargeItems.length > 0) {
    const updatedCharges: ChargeItem[] = [];
    const billingRows = input.after.billingCodes ?? [];
    for (const ci of chargeItems) {
      if (!ci.id) continue;
      const row =
        billingRows.find((r) => r.chargeItemId === ci.id) ??
        billingRows[chargeItems.indexOf(ci)];
      if (!row) continue;
      const updated = applyMetadataBillingToChargeItem(ci, row);
      if (JSON.stringify(updated) !== JSON.stringify(ci)) {
        try {
          updatedCharges.push(await medplum.updateResource(updated));
        } catch (err) {
          syncFailures.push(
            `ChargeItem/${ci.id} update failed (${formatMedplumClientError(err)})`,
          );
        }
      }
    }
    if (updatedCharges.length > 0) result.chargeItems = updatedCharges;
  }

  try {
    await recordMetadataAuditEvent(medplum, {
      questionnaireResponseId: input.questionnaireResponseId,
      appointmentId: appointment.id,
      changedFields,
      agentReference: input.agentReference,
    });
  } catch (err) {
    // Providers often lack AuditEvent create in AccessPolicy; note snapshot save still succeeded.
    console.warn('Note metadata audit log skipped:', formatMedplumClientError(err));
  }

  if (syncFailures.length > 0) {
    throw new Error(syncFailures.join('; '));
  }

  return result;
}
